import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import Proptypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Heading from "@/components/heading";
import HSubmitRenewalForm from "@/components/quote-card";
import {
  fetchQuotes,
  getRenewalDetailsDataSelector,
} from "@/features/quote/quoteSlice";
import { formatDate, isMobile, toAmount } from "@/helpers/utils";
import debounce from "lodash/debounce";
import { IMAGE_BASE_PATH, GLOBALUID_GENERATOR } from "@/config/config";

import { bgQuote, center } from "./quote.module.scss";
import Link from "next/link";
import { isLoaderActive } from "@/features/loader/loaderSlice";
import { useTranslation } from "react-i18next";
import OffcanvasMenu from "@/components/quote-card/modals/OffCanvasMenu";
import RadioInput from "@/components/core/form/RadioInput";
import { formCard, upsellFormTitle } from "@/assets/styles/common.module.scss";
import { LabelDropDown } from "@/components/core/form";
import {
  RADIO_OPTIONS,
  INTENT,
  PRODUCT_CODE,
  MEDICARE_GENERIC_CODES,
  HEALTH_SUPERCHARGE_CODE,
} from "@/config/constants";
import Image from "next/image";
import {
  getCustomerType,
  getMetadata,
} from "@/features/metadata/metadataSlice";
import { EVENTS } from "@/config/events";
import { invokeMoengageAnalytics } from "@/utils/moengage";
import moment from "moment";
import axios from "axios";
import {
  getIsApp,
  getProductCode,
  setProductCode,
  setUtmMedium,
  getMobileNumber,
} from "@/features/quote/quoteSlice";
import { useRouter } from "next/router";

function calculateUniqueSumInsuredArray(premiumSchemes, selectedTenure) {
  if (!premiumSchemes || premiumSchemes.length === 0 || !selectedTenure) {
    return [];
  }

  const selectedTenureSIArr = premiumSchemes.filter(
    (scheme) => scheme.tenure === selectedTenure
  );

  if (!selectedTenureSIArr || selectedTenureSIArr.length === 0) {
    return [];
  }

  const uniquePolicySchemeSumInsuredArray = [];
  const uniquePolicySchemeSumInsuredSet = new Set();

  selectedTenureSIArr.forEach((scheme) => {
    const policySchemeSumInsured =
      scheme?.memberPremium?.m1?.policySchemeSumInsured;
    const formattedValue = (policySchemeSumInsured / 100000).toFixed(2); // Format value to lakhs
    const formattedValueWithRupee = `₹ ${
      formattedValue.endsWith(".00") ? parseInt(formattedValue) : formattedValue
    } Lakhs`;

    if (!uniquePolicySchemeSumInsuredSet.has(formattedValueWithRupee)) {
      uniquePolicySchemeSumInsuredSet.add(formattedValueWithRupee);
      uniquePolicySchemeSumInsuredArray.push({
        id: scheme.schemeType,
        value: formattedValueWithRupee, // Store value in lakhs
        valueForSort: policySchemeSumInsured, // Store value in lakhs
        schemeType: scheme.schemeType,
        schemeObj: scheme,
      });
    }
  });

  uniquePolicySchemeSumInsuredArray.sort(
    (a, b) => a.valueForSort - b.valueForSort
  );

  return uniquePolicySchemeSumInsuredArray;
}

function RenewalQuotes(props) {
  const {
    policyNo,
    utmSource,
    utmMedium: utmMediumFromProps,
    utmCampaign,
    utmContent,
    utmTerm,
    schemes,
    certificateNo,
  } = props;
  const userType = useSelector(getCustomerType);
  const metadata = useSelector(getMetadata);
  const resource = metadata.resource;
  const isCustomer = userType?.toLowerCase() === "customer";
  const utmMediumFromStore = useSelector(getIsApp);
  const productCodeFromStore = useSelector(getProductCode);
  const mobileNumberFromStore = useSelector(getMobileNumber);
  const dispatch = useDispatch();
  const router = useRouter();
  const effectiveUtmMedium =
    utmMediumFromStore ||
    utmMediumFromProps ||
    router.query?.utm_medium ||
    router.query?.utmMedium ||
    "";

  useEffect(() => {
    if (!router.isReady) return;

    const utmMediumFromQuery =
      router.query?.utm_medium || router.query?.utmMedium;

    if (utmMediumFromQuery) {
      dispatch(setUtmMedium(utmMediumFromQuery));
    }
  }, [
    router.isReady,
    router.query?.utm_medium,
    router.query?.utmMedium,
    dispatch,
  ]);
  const { t } = useTranslation();

  const renewalDetailsData = useSelector(getRenewalDetailsDataSelector);
  const isLoading = useSelector(isLoaderActive);
  const [globalUid, setGlobalUid] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = renewalDetailsData?.productCode;
    if (code !== undefined && code !== null && code !== "") {
      dispatch(setProductCode(String(code)));
    }
  }, [dispatch, renewalDetailsData?.productCode]);

 const handleGenerate = async () => {
   try {
     const mobileNumber =
       mobileNumberFromStore ||
       renewalDetailsData?.proposerDetails?.mobileNumber;

     if (!mobileNumber) {
       setError("Mobile number is missing.");
       return;
     }

     const response = await axios.post(
       GLOBALUID_GENERATOR,
       {
         mobile: mobileNumber,
       },
       {
         headers: {
           "Content-Type": "application/json",
           Accept: "application/json",
         },
       }
     );

     const globalUid = response?.data?.data?.globalUid;

     if (window?.Moengage && globalUid) {
       window.Moengage.destroy_session();
       window.Moengage.add_unique_user_id(globalUid);
       window.Moengage.add_mobile(mobileNumber);
     }

     setGlobalUid(globalUid);
     setError(null);
   } catch (err) {
     console.error("Error generating Global UID:", err);
     setError("Failed to generate Global UID.");
   }
 };

  // const dimensions = useWindowDimensions();
  const [dimensions, setDimensions] = useState({
    height: 0,
    width: 0,
  });

  const [isMobileView, setIsMobileView] = useState(false);
  useEffect(() => {
    setIsMobileView(isMobile(window.innerWidth));
  }, []);

  const [sendLinkCustomer, setSendLinkCustomer] = useState(true);

  useEffect(() => {
    if (policyNo) {
      const quoteTags = {
        policyNo,
        utmSource,
        utmMedium: effectiveUtmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        schemes,
        certificateNo,
        resource,
      };
      dispatch(fetchQuotes(quoteTags));
    }
  }, [
    certificateNo,
    dispatch,
    policyNo,
    schemes,
    utmCampaign,
    utmContent,
    effectiveUtmMedium,
    utmSource,
    utmTerm,
    resource,
  ]);

  const [selectedValue, setSelectedValue] = useState("2"); // Initial selected value
  const [producerUpsell, setProducerUpsell] = useState(false); // Initial selected value
  const [upsellData, setUpsellData] = useState(null);
  let isMedicareAddOn =
    MEDICARE_GENERIC_CODES.includes(renewalDetailsData?.productCode) &&
    renewalDetailsData?.isAddOnJourney;
  const calculateBenefit = useCallback(
    (upsellScheme) => {
      if (upsellScheme) {
        const selectedScheme = renewalDetailsData.premiumSchemes?.find(
          (schema) => {
            return (
              schema.schemeType.replace(/\s/g, "").toLowerCase() === "asis"
            );
          }
        );
        const asIsPerLacPremium =
          (selectedScheme.totalPremium /
            selectedScheme.memberPremium.m1.policySchemeSumInsured) *
          100000;
        const upsellPerLacPremium =
          ((upsellScheme.totalPremium /
            upsellScheme.memberPremium.m1.policySchemeSumInsured) *
            100000) /
          upsellScheme.tenure;
        if (upsellPerLacPremium < asIsPerLacPremium) {
          const percentageSaved =
            ((asIsPerLacPremium - upsellPerLacPremium) / asIsPerLacPremium) *
            100;
          // console.log(Math.round(percentageSaved));
          setSavedPlanPercentage(Math.round(percentageSaved));
        } else {
          upsellScheme.tenure === 2
            ? setSavedPlanPercentage(Math.round(5))
            : upsellScheme.tenure === 3
            ? setSavedPlanPercentage(Math.round(10))
            : null;
          return;
        }
      }
    },
    [renewalDetailsData?.premiumSchemes]
  );

  const hasTenure = useCallback(
    (tenureValue) => {
      // Check if tenureValue exists in the premiumSchemes array
      return renewalDetailsData.premiumSchemes.some(
        (scheme) => scheme.tenure === parseInt(tenureValue, 10)
      );
    },
    [renewalDetailsData?.premiumSchemes]
  );

  const handleRadioChange = useCallback(
    (event) => {
      if (hasTenure(event.target.value)) {
        setSelectedValue(event.target.value);
        const uniqueSIArray = calculateUniqueSumInsuredArray(
          renewalDetailsData.premiumSchemes,
          parseInt(event.target.value)
        );

        setSelectedSumInsured(uniqueSIArray);
        setCurrentSumInsured(uniqueSIArray?.[0]?.value?.toString());
        if (
          uniqueSIArray?.[0]?.schemeType?.replace(/\s/g, "").toLowerCase() ===
          "asis"
        ) {
          setSelectedPlanOption("option1");
          setUpsellData(null);
        } else {
          setUpsellData(uniqueSIArray?.[0]?.schemeObj);
          calculateBenefit(uniqueSIArray?.[0]?.schemeObj);
        }
      } else {
        return;
      }
    },
    [hasTenure, renewalDetailsData?.premiumSchemes, calculateBenefit]
  );

  const [selectedSumInsured, setSelectedSumInsured] = useState("");
  const [currentSumInsured, setCurrentSumInsured] = useState("");
  const [selectedPlanOption, setSelectedPlanOption] = useState("option1");
  const [savedPlanPercentage, setSavedPlanPercentage] = useState(null);
  const [customerUpsell, setCustomerUpsell] = useState(false);
  const [offeredScheme, setOfferedScheme] = useState(null);
  const [higherSIUpgrade, setHigherSIUpgrade] = useState(null);
  const [nextSlabSI, setNextSlabSI] = useState(null);
  const productCode = [2833, 2896, 2888, 2848];

  const handleSumInsuredSelect = useCallback(
    (event) => {
      const selectedSI = selectedSumInsured.filter(
        (obj) => obj.schemeType === event.target.value
      );
      setCurrentSumInsured(selectedSI?.[0]?.value?.toString());
      if (event.target.value.replace(/\s/g, "").toLowerCase() === "asis") {
        setSelectedPlanOption("option1");
        setUpsellData(null);
      } else {
        const upsellArr = renewalDetailsData.premiumSchemes.filter(
          (obj) => obj.schemeType === event.target.value
        );
        if (upsellArr.length > 0) {
          setUpsellData(upsellArr?.[0]);
          calculateBenefit(upsellArr?.[0]);
        }
      }
    },
    [
      selectedSumInsured,
      setCurrentSumInsured,
      setSelectedPlanOption,
      setUpsellData,
      renewalDetailsData,
      calculateBenefit,
    ]
  );

  useEffect(() => {
    const isValueEqual = (value, compareTo) =>
      value?.trim().toLowerCase() === compareTo;

    if (!renewalDetailsData || !renewalDetailsData.premiumSchemes) return;

    const isAgent = userType?.toLowerCase() === "agent";
    const isCustomer = userType?.toLowerCase() === "customer";
    const hasMultipleSchemes = renewalDetailsData.premiumSchemes.length >= 1;
    const isProductValid = productCode.includes(
      renewalDetailsData?.productCode
    );
    const shouldProcessAgent = isAgent && isProductValid && hasMultipleSchemes;
    const shouldProcessCustomer =
      isCustomer && isProductValid && hasMultipleSchemes;
    const isRetention =
      (isValueEqual(metadata.utm_source, "retention") ||
        isValueEqual(metadata.utm_source, "super_app")) &&
      isProductValid;

    if (isRetention) {
      setSendLinkCustomer(false);
    }

    if (shouldProcessAgent || isRetention) {
      processAgentOrRetentionScenario();
    } else if (shouldProcessCustomer) {
      handleGenerate();
      invokeMoengageAnalytics(EVENTS.PAGE_VIEW, {
        "Page View": "Quote Page",
        "Product Code": renewalDetailsData?.productCode?.toString() || productCodeFromStore?.toString() || "",
        "is_app": effectiveUtmMedium?.toLowerCase()?.includes("mobile_app") ? "Yes" : "No",
      });
      processCustomerScenario();
    }

    function findSchemeByType(type) {
      return renewalDetailsData.premiumSchemes.find(
        (scheme) => scheme.schemeType.replace(/\s/g, "").toLowerCase() === type
      );
    }

    function processAgentOrRetentionScenario() {
      const selectedAsIsScheme = findSchemeByType("asis");
      if (!selectedAsIsScheme) return;

      setProducerUpsell(true);
      setSelectedValue(selectedAsIsScheme.tenure?.toString());
      const uniqueSIArray = calculateUniqueSumInsuredArray(
        renewalDetailsData.premiumSchemes,
        selectedAsIsScheme.tenure
      );
      setSelectedSumInsured(uniqueSIArray);
      setCurrentSumInsured(uniqueSIArray[0]?.value?.toString());
      setSelectedPlanOption("option1");
      setUpsellData(null);
    }

    function processCustomerScenario() {
      const selectedScheme = findSchemeByType("asis");
      if (isMedicareAddOn) {
        invokeMoengageAnalytics(
          EVENTS.EVENT_RENEWAL_QUOTE_PAGE_RECOMMENDED_PLAN_SELECTION,
          {
            "Policy No": renewalDetailsData?.policyNo || "",
            "Product Name": renewalDetailsData?.productName || "",
            "Product Code": renewalDetailsData?.productCode?.toString() || productCodeFromStore?.toString() || "",
            "Mobile Number": mobileNumberFromStore ||
              renewalDetailsData?.proposerDetails?.mobileNumber || "",
            DOB: renewalDetailsData?.proposerDetails?.dob || "",
            Variant: renewalDetailsData?.variant || "",
            // "Scheme Type": selectedScheme?.schemeType || "",
            "Total Premium":
              renewalDetailsData?.premiumSchemes?.[1]?.premium || "",
            Package: Object.values(selectedScheme?.packages || {})
              .filter((pkg) => pkg.isOpted && pkg.isMandatory)
              .map((pkg) => pkg?.name),
            Tenure: selectedScheme?.tenure || "",
            // "Sum Insured":  renewalDetailsData?.memberDetails?.[0]?.applicableSI || "",
            "Producer Code": renewalDetailsData?.producerCode || "",
            "is_app": effectiveUtmMedium?.toLowerCase()?.includes("mobile_app") ? "Yes" : "No",
          }
        );
      } else {
        invokeMoengageAnalytics(
          EVENTS.EVENT_RENEWAL_QUOTE_PAGE_DEFAULT_PLAN_SELECTION,
          {
            "Policy No": renewalDetailsData?.policyNo || "",
            "Product Name": renewalDetailsData?.productName || "",
            "Product Code": renewalDetailsData?.productCode?.toString() || productCodeFromStore?.toString() || "",
            "Mobile Number": mobileNumberFromStore || renewalDetailsData?.proposerDetails?.mobileNumber || "",
            DOB: renewalDetailsData?.proposerDetails?.dob || "",
            Variant: renewalDetailsData?.variant || "",
            "Scheme Type": selectedScheme?.schemeType || "",
            "Total Premium": selectedScheme?.totalPremium || "",
            Package: Object.values(selectedScheme?.packages || {})
              .filter((pkg) => pkg.isOpted && pkg.isMandatory)
              .map((pkg) => pkg?.name),
            Tenure: selectedScheme?.tenure || "",
            "Sum Insured":
              renewalDetailsData.memberDetails[0].applicableSI || "",
            "Producer Code": renewalDetailsData?.producerCode || "",
            "is_app": effectiveUtmMedium?.toLowerCase()?.includes("mobile_app") ? "Yes" : "No",
          }
        );
      }
      if (!selectedScheme) return;

      const tenure = selectedScheme.tenure;
      if (!tenure || tenure >= 3) return;

      const schemeType = tenure === 1 ? "tenure2year" : "tenure3year";
      const offeredScheme = renewalDetailsData.premiumSchemes.find(
        (scheme) =>
          scheme.schemeType.replace(/\s/g, "").toLowerCase() === schemeType
      );
      if (!offeredScheme) return;

      setOfferedScheme(offeredScheme);

      const highersislab =
        tenure === 1 ? "highersislab1" : "nextslabpremium2year";
      const higherSIUpgrade = renewalDetailsData.premiumSchemes.find(
        (scheme) =>
          scheme.schemeType.replace(/\s/g, "").toLowerCase() === highersislab
      );
      // if (!higherSIUpgrade) return;

      setHigherSIUpgrade(higherSIUpgrade);

      const nextSlabType =
        offeredScheme.tenure === 2
          ? "nextslabpremium2year"
          : "nextslabpremium3year";
      const nextSlabPremiumUpgrade = renewalDetailsData.premiumSchemes.find(
        (scheme) =>
          scheme.schemeType.replace(/\s/g, "").toLowerCase() === nextSlabType
      );
      // if (!nextSlabPremiumUpgrade) return;

      setNextSlabSI(nextSlabPremiumUpgrade);
      setCustomerUpsell(true);
    }
  }, [metadata, renewalDetailsData, userType]);

  isMedicareAddOn =
    MEDICARE_GENERIC_CODES.includes(renewalDetailsData?.productCode) &&
    renewalDetailsData?.isAddOnJourney;
  const getDaysLeft = (expiringDate) => {
    const today = moment().startOf("day");
    const expiringPolicyDate = moment(expiringDate).startOf("day");
    return expiringPolicyDate.diff(today, "days");
  };

  return !isEmpty(renewalDetailsData) ? (
    <>
      <div
        className={classNames("container", {
          cardUpsellScreenHeight: producerUpsell,
          mobileUpsellHeight: isMobileView && upsellData,
        })}
      >
        {isMedicareAddOn ? (
          <>
            <Heading
              title={`${t("choose_plan_heading")}`}
              headerWidth="col-md-8 col-sm-12"
              upsellTitle={true}
            />
            <div className="policy-info-card">
              <div className="policy-info">
                <span className="policy-label">Plan</span>
                <span className="policy-value">
                  {["Protect - MediCare", "MediCare Protect"].includes(
                    renewalDetailsData.variant
                  )
                    ? "MediCare" :
                  renewalDetailsData?.productCode === Number(HEALTH_SUPERCHARGE_CODE) ? `${renewalDetailsData?.productName} ${renewalDetailsData?.variant}`
                    : renewalDetailsData.variant}
                </span>
              </div>

              <div className="separator"></div>

              <div className="policy-info">
                <span className="policy-label">Policy Number</span>
                <span className="policy-value">
                  {renewalDetailsData.policyNo}
                </span>
              </div>

              <div className="separator"></div>

              <div className="policy-info">
                <span className="policy-label">Policy Type</span>
                <span className="policy-value">
                  {renewalDetailsData.planType}
                </span>
              </div>

              <div className="separator"></div>

              <div className="policy-info">
                <span className="policy-label">Renewal Date</span>
                <span className="policy-value">
                  {formatDate(
                    renewalDetailsData.expiringPolicyToDate,
                    "DD MMM, YYYY"
                  )}
                  <span className="days-left">
                    {(() => {
                      const daysLeft = getDaysLeft(
                        renewalDetailsData.expiringPolicyToDate
                      );

                      if (daysLeft < 0) return "(In Grace Period)";
                      if (daysLeft === 0) return "(Last Day for Renewal)";
                      return `(${daysLeft} Days Left)`;
                    })()}
                  </span>
                </span>
              </div>

              <div className="separator"></div>

              <div className="policy-info">
                <span className="policy-label">Covers</span>
                <span className="policy-value">
                  {renewalDetailsData.memberDetails.length} Member
                  {renewalDetailsData.memberDetails.length > 1 && <>s</>}
                </span>
              </div>
              <div className="separator"></div>
              <div className="policy-info">
                <span className="link-btn">
                  <OffcanvasMenu data={renewalDetailsData} />
                </span>
              </div>
            </div>
            {/* <div className="row">
              <div className="col-md-12 additional-detail-section">
                <span className=" upsell-label">
                  <OffcanvasMenu data={renewalDetailsData} />
                </span>
              </div>
            </div> */}
            {/* <div className="row">
              <div className="quoteInfoCard">
                <div>
                  <label>Product:</label>
                  <span>Medicare Plus</span>
                </div>
                <div>
                  <label>Policy Number:</label>
                  <span>9897878767545</span>
                </div>
                <div>
                  <label>Policy Type:</label>
                  <span>Individual</span>
                </div>
                <div>
                  <label>Renewal Due Date:</label>
                  <span>03/06/2023 (10 Days Left)</span>
                </div>
                <div>
                  <label>Covers:</label>
                  <span>5 Members</span>
                </div>
                <div>
                  <button>View Additional Details</button>
                </div>
              </div>
            </div> */}
            <HSubmitRenewalForm
              nextPageUrl={
                renewalDetailsData?.productCode === 2896 && !isCustomer
                  ? "proposal-edit"
                  : "proposal"
              }
              renewalDetails={renewalDetailsData}
              offeredScheme={offeredScheme}
              customerUpsell={customerUpsell}
              producerUpsell={producerUpsell}
              higherSIUpgrade={higherSIUpgrade}
              nextSlabSI={nextSlabSI}
              policyNumber={renewalDetailsData.policyNo}
            />
          </>
        ) : customerUpsell ? (
          <>
            <Heading
              title={`${t("personalised_plan_heading")}`}
              headerWidth="col-md-8 col-sm-12"
              upsellTitle={true}
            />
            {!isMobileView ? (
              <div className="row">
                <div className="col-md-12">
                  <span className="rContentClass upsell-label">
                    {`${t("policy_number")}: `}
                  </span>
                  <span className="rContentClass upsell-value">
                    {renewalDetailsData.policyNo}
                  </span>
                  <span className="rContentClass policyInfoSection upsell-label">
                    {`${t("renewal_due_date")}: `}
                  </span>
                  <span className="rContentClass upsell-value">
                    {formatDate(
                      renewalDetailsData.expiringPolicyToDate,
                      "DD MMM, YYYY"
                    )}
                  </span>
                  <span className="policyInfoSection">
                    <Image
                      src={`${IMAGE_BASE_PATH}/images/Separator.svg`}
                      alt="X"
                      width={10}
                      height={20}
                    />
                  </span>
                  <span className="policyInfoSection upsell-label">
                    <OffcanvasMenu data={renewalDetailsData} />
                  </span>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-12">
                  <span className="rContentClass upsell-label">
                    {`${t("policy_number")}: `}
                    <span className="rContentClass">
                      {renewalDetailsData.policyNo}
                    </span>
                  </span>
                </div>
                <div className="col-12">
                  <span className="rContentClass upsell-label">
                    {`${t("renewal_due_date")}: `}
                    <span className="rContentClass">
                      {formatDate(
                        renewalDetailsData.expiringPolicyToDate,
                        "DD MMM, YYYY"
                      )}
                    </span>
                  </span>
                </div>
                <div className="col-12 upsell-label">
                  <OffcanvasMenu data={renewalDetailsData} />
                </div>
              </div>
            )}
            <HSubmitRenewalForm
              nextPageUrl={
                renewalDetailsData?.productCode === 2896 && !isCustomer
                  ? "proposal-edit"
                  : "proposal"
              }
              renewalDetails={renewalDetailsData}
              offeredScheme={offeredScheme}
              customerUpsell={customerUpsell}
              higherSIUpgrade={higherSIUpgrade}
              nextSlabSI={nextSlabSI}
              policyNumber={renewalDetailsData.policyNo}
            />
          </>
        ) : producerUpsell ? (
          <>
            <Heading
              title={`${t("choose_plan_heading")}`}
              headerWidth="col-md-8 col-sm-12"
              upsellTitle={true}
            />
            {!isMobileView ? (
              <div className="row">
                <div className="col-md-12">
                  <span className="rContentClass upsell-label">
                    {`${t("policy_number")}: `}
                  </span>
                  <span className="rContentClass upsell-value">
                    {renewalDetailsData.policyNo}
                  </span>
                  <span className="rContentClass policyInfoSection upsell-label">
                    {`${t("renewal_due_date")}: `}
                  </span>
                  <span className="rContentClass upsell-value">
                    {formatDate(
                      renewalDetailsData.expiringPolicyToDate,
                      "DD MMM, YYYY"
                    )}
                  </span>
                  <span className="policyInfoSection">
                    <Image
                      src={`${IMAGE_BASE_PATH}/images/Separator.svg`}
                      alt="X"
                      width={10}
                      height={20}
                    />
                  </span>
                  <span className="policyInfoSection upsell-label">
                    <OffcanvasMenu data={renewalDetailsData} />
                  </span>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-12">
                  <span className="rContentClass upsell-label">
                    {`${t("policy_number")}: `}
                    <span className="rContentClass">
                      {renewalDetailsData.policyNo}
                    </span>
                  </span>
                </div>
                <div className="col-12">
                  <span className="rContentClass upsell-label">
                    {`${t("renewal_due_date")}: `}
                    <span className="rContentClass">
                      {formatDate(
                        renewalDetailsData.expiringPolicyToDate,
                        "DD MMM, YYYY"
                      )}
                    </span>
                  </span>
                </div>
                <div className="col-12 upsell-label">
                  <OffcanvasMenu data={renewalDetailsData} />
                </div>
              </div>
            )}
            <div className="row m-0">
              <div
                className={classNames(
                  formCard,
                  "rQuoteCard upsellCard",
                  "mt-4"
                )}
              >
                <label
                  htmlFor=""
                  className={classNames("mb-2", upsellFormTitle)}
                >
                  {`${t("customise_plan_heading")}`}
                </label>
                <div className="row">
                  <div
                    className={`col-lg-5 col-md-7 ${
                      isMobileView ? "mb-2" : ""
                    }`}
                  >
                    <label htmlFor="tenure" className="mb-3 head-sm">
                      {`${t("policy_tenure")}`}
                    </label>
                    <div className="radio-container">
                      {RADIO_OPTIONS.map((option) => (
                        <RadioInput
                          key={option.value}
                          id={`option-${option.value}`}
                          name="duration"
                          value={option.value}
                          label={option.label}
                          checked={selectedValue === option.value}
                          onChange={handleRadioChange}
                          addInfo={option.addInfo}
                          disabled={!hasTenure(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                  {renewalDetailsData?.productCode !== 2833 && (
                    <>
                      {!isMobileView && (
                        <>
                          {" "}
                          <div className="w-auto border-sep">
                            <Image
                              src={`${IMAGE_BASE_PATH}/images/Plus-sign.svg`}
                              alt="X"
                              width={25}
                              height={68}
                            />
                          </div>
                        </>
                      )}
                      <div className="col-xl-3 col-lg-3 col-md-4">
                        <label htmlFor="sumInsuredInput" className="head-sm">
                          {`${t("sum_insured_titlecase")}`}
                        </label>
                        <LabelDropDown
                          dropdownNm="sumInsuredDropdown"
                          title={currentSumInsured}
                          items={selectedSumInsured}
                          onSelect={handleSumInsuredSelect}
                          childClass={isMobileView ? "w-50" : ""}
                          sumInsuredDropdown={true}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <HSubmitRenewalForm
              nextPageUrl={
                renewalDetailsData?.productCode === 2896 && !isCustomer
                  ? "proposal-edit"
                  : "proposal"
              }
              renewalDetails={renewalDetailsData}
              upsellData={upsellData}
              producerUpsell={producerUpsell}
              selectedPlanOption={selectedPlanOption}
              onSelectedOptionChange={setSelectedPlanOption}
              savedPlanPercentage={savedPlanPercentage}
              policyNumber={renewalDetailsData.policyNo}
            />
          </>
        ) : (
          <>
            <Heading
              title={"Here's Your Health Insurance Premium"}
              headerWidth="col-md-8 col-sm-12"
            />
            <div
              className={classNames("container-fluid", bgQuote)}
              style={{
                backgroundImage: !isMobileView
                  ? `url(${IMAGE_BASE_PATH}/images/health_i_d_quote.svg)`
                  : "",
              }}
            >
              <div
                className={classNames("container", {
                  "ps-4": !isMobileView,
                })}
              >
                <div>
                  {resource === INTENT.GROUP_RENEW_QUOTE ? (
                    <>
                      {" "}
                      <section className="col-md-10 mt-3">
                        <div className="row">
                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Master Policy Number :
                              <span className="rContentClass upsell-value">
                                {" "}
                                {renewalDetailsData.masterPolicyNo}
                              </span>
                            </label>
                          </div>
                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Certificate Number :
                              <span className="rContentClass upsell-value">
                                {" "}
                                {renewalDetailsData.certificateNo}
                              </span>
                            </label>
                          </div>
                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Partner Application Number :
                              <span className="rContentClass upsell-value">
                                {" "}
                                {renewalDetailsData.partnerApplicationNo
                                  ? renewalDetailsData.partnerApplicationNo
                                  : "-"}
                              </span>
                            </label>
                          </div>
                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Sum Insured :
                              <span className="rContentClass upsell-value">
                                {" "}
                                ₹
                                {toAmount(
                                  Math.round(
                                    renewalDetailsData.memberDetails[0]
                                      .applicableSI
                                  )
                                )}{" "}
                              </span>
                            </label>
                          </div>
                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Cumulative Bonus:{" "}
                              <span className="rContentClass upsell-value">
                                {renewalDetailsData.memberDetails[0].cbAmount
                                  ? `₹${toAmount(
                                      Math.round(
                                        renewalDetailsData.memberDetails[0]
                                          .cbAmount
                                      )
                                    )}`
                                  : "NA"}
                              </span>
                            </label>
                          </div>

                          <div className="col-md-6 col-lg-4">
                            <label
                              htmlFor=""
                              className="rContentClass upsell-label"
                            >
                              Renewal Due Date :
                              <span className="rContentClass upsell-value">
                                {" "}
                                {formatDate(
                                  renewalDetailsData.expiringPolicyToDate,
                                  "DD MMM, YYYY"
                                )}
                              </span>
                            </label>
                          </div>
                          {toAmount(
                            Math.round(
                              renewalDetailsData.memberDetails[0].deductible
                            )
                          ) && (
                            <div className="col-md-6 col-lg-4">
                              <label
                                htmlFor=""
                                className="rContentClass upsell-label"
                              >
                                Deductible :
                                <span className="rContentClass upsell-value">
                                  {" "}
                                  ₹
                                  {toAmount(
                                    Math.round(
                                      renewalDetailsData.memberDetails[0]
                                        .deductible
                                    )
                                  )}
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      </section>
                    </>
                  ) : (
                    <>
                      {" "}
                      <section className="col-md-7 col-sm-12">
                        <article className="detail-container">
                          <hr className="m-0 my-1 d-md-none" />
                          <div className="mAlignCenter">
                            <span className="rContentClass">
                              {`${t("policy_number")}: `}
                            </span>
                            <span className="rContentClass">
                              {renewalDetailsData.policyNo}
                            </span>
                          </div>
                          <div>
                            <div className="mAlignCenter">
                              <span className="rContentClass">
                                {`${t("renewal_due_date")}: `}
                              </span>
                              <span className="rContentClass">
                                {formatDate(
                                  renewalDetailsData.expiringPolicyToDate,
                                  "DD MMM, YYYY"
                                )}
                              </span>
                            </div>
                            <div>
                              <OffcanvasMenu data={renewalDetailsData} />
                            </div>
                          </div>
                          <hr className="m-0 my-1 d-none d-md-block" />
                        </article>
                      </section>
                    </>
                  )}
                </div>

                <br />
                <section className="col-md-5 col-sm-12">
                  <HSubmitRenewalForm
                    className="mt-3"
                    nextPageUrl={
                      renewalDetailsData?.productCode === 2896 && !isCustomer
                        ? "proposal-edit"
                        : "proposal"
                    }
                    renewalDetails={renewalDetailsData}
                    schemes={schemes}
                    resource={resource}
                    sendLinkCustomer={sendLinkCustomer}
                    selectedPlanOption={selectedPlanOption}
                    policyNumber={renewalDetailsData.policyNo}
                  />
                </section>
              </div>
            </div>{" "}
          </>
        )}
      </div>
    </>
  ) : !isLoading ? (
    <div className={center}>
      No data found
      <br />
      <Link href="/">
        <u>Go Back</u>
      </Link>
    </div>
  ) : (
    ""
  );
}

RenewalQuotes.propTypes = {
  policyNo: Proptypes.string,
  utmSource: Proptypes.string,
  utmMedium: Proptypes.string,
  utmCampaign: Proptypes.string,
  utmContent: Proptypes.string,
  utmTerm: Proptypes.string,
  schemes: Proptypes.string,
  certificateNo: Proptypes.string,
};

RenewalQuotes.defaultProps = {
  policyNo: "",
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  utmContent: "",
  utmTerm: "",
  schemes: "",
  certificateNo: "",
};
RenewalQuotes.getInitialProps = async ({ query }) => {
  return {
    policyNo: query.policyNo,
    utmSource: query.utmSource,
    utmMedium: query.utmMedium,
    utmCampaign: query.utmCampaign,
    utmContent: query.utmContent,
    utmTerm: query.utmTerm,
    schemes: query.schemes,
    certificateNo: query.certificateNo,
  };
};

export default RenewalQuotes;
